import { useMemo, useState } from 'react'
import { loanCapitalProgram } from '@/pages/pipeline-intelligence/dealResolution'
import { Banknote, Calculator, ChevronRight, ClipboardList, Landmark, Lock, Send, Unlock } from 'lucide-react'
import { useAntarcticaDeals } from '@/hooks/useDemoDataAccess'
import { formatUsd } from '@/utils/money'
import type { Loan } from '@/types'

/**
 * Funding / Closing on the live partition.
 *
 * The screen used to render, for every account: $200,900 of hardcoded closing
 * fees (with 0 deals in closing), a checklist due in April 2026, an audit
 * trail of changes "by Sarah Chen / Marcus Webb / auto_doc_trigger", every
 * click logged as a named person and never saved, "2 items auto-triggered
 * today", a 12% mezzanine plug and placeholder wire instructions for "First
 * National Bank" (CRE-19, LPR-15, MOD-16). None of that is recorded anywhere.
 *
 * Live shows only what the deal records: the deals in a closing stage, their
 * amount and stage. Each panel with no source says so. Wire instructions in
 * particular are never shown from a placeholder.
 */

const CLOSING_STAGES = ['IC / Committee Approved', 'Rate Lock'] as const

function isClosingStage(loan: Loan): boolean {
  return (CLOSING_STAGES as readonly string[]).includes(loan.stage)
}

function NoRecord({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-amber-400/10 px-3 py-4 text-center text-[11px] leading-relaxed text-stone-500">
      {children}
    </div>
  )
}

export default function ClosingLive() {
  const deals = useAntarcticaDeals('closing')
  const closingDeals = useMemo(() => deals.filter(isClosingStage), [deals])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const selected = closingDeals.find((d) => d.id === selectedId) ?? closingDeals[0]

  return (
    <div className="h-full space-y-3 px-4 py-3">
      <div className="panel">
        <div className="panel-header">
          <span className="panel-header-title flex items-center gap-2">
            <Landmark size={13} className="text-amber-400/60" />
            Closing Pipeline
          </span>
          <span className="font-mono text-[10px] text-stone-500">
            {closingDeals.length} deal{closingDeals.length === 1 ? '' : 's'}
          </span>
        </div>
        {closingDeals.length === 0 ? (
          <div className="p-3">
            <NoRecord>
              No deal is in a closing stage (IC / Committee Approved or Rate Lock). Deals appear here when they reach
              one.
            </NoRecord>
          </div>
        ) : (
          <div className="max-h-[220px] overflow-auto">
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th className="text-right">Amount</th>
                  <th>Product</th>
                  <th>Stage</th>
                  <th>Rate Lock</th>
                </tr>
              </thead>
              <tbody>
                {closingDeals.map((deal) => (
                  <tr
                    key={deal.id}
                    className={`cursor-pointer ${deal.id === selected?.id ? 'selected' : ''}`}
                    onClick={() => setSelectedId(deal.id)}
                  >
                    <td className="max-w-[220px] truncate">
                      <div className="flex items-center gap-1.5">
                        {deal.id === selected?.id && <ChevronRight size={10} className="shrink-0 text-amber-400" />}
                        <span className={deal.id === selected?.id ? 'text-amber-300' : ''}>{deal.property}</span>
                      </div>
                    </td>
                    <td className="text-right font-mono text-[11px]">{formatUsd(deal.loanAmount)}</td>
                    <td className="text-[11px]">{loanCapitalProgram(deal) ?? '—'}</td>
                    <td className="text-[11px] text-stone-400">{deal.stage}</td>
                    <td>
                      {deal.stage === 'Rate Lock' ? (
                        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400">
                          <Lock size={10} /> Locked
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] text-stone-500">
                          <Unlock size={10} /> Not locked
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title flex items-center gap-2">
              <ClipboardList size={13} className="text-amber-400/60" />
              Closing Checklist
            </span>
            {selected && <span className="font-mono text-[10px] text-stone-500">{selected.property}</span>}
          </div>
          <div className="panel-body">
            <NoRecord>
              No closing checklist is recorded{selected ? ` for ${selected.property}` : ''}, so there are no items,
              due dates or audit trail to show. Documents received for a deal are tracked on its dossier.
            </NoRecord>
          </div>
        </div>

        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title flex items-center gap-2">
              <Calculator size={13} className="text-amber-400/60" />
              Fees, Sources &amp; Uses, Wire
            </span>
          </div>
          <div className="panel-body space-y-3">
            <div>
              <div className="mb-1 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.14em] text-stone-500">
                <Calculator size={11} className="text-amber-400/50" /> Closing costs
              </div>
              <NoRecord>No closing fee schedule is recorded; no fee is estimated in its place.</NoRecord>
            </div>
            <div>
              <div className="mb-1 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.14em] text-stone-500">
                <Banknote size={11} className="text-amber-400/50" /> Sources &amp; uses
              </div>
              <NoRecord>A deal&apos;s sources and uses, as stated in its package, are on its dossier.</NoRecord>
            </div>
            <div>
              <div className="mb-1 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.14em] text-stone-500">
                <Send size={11} className="text-amber-400/50" /> Wire instructions
              </div>
              <NoRecord>
                No wire instructions are recorded. Wire details are only ever shown from a verified record, never from
                a placeholder.
              </NoRecord>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
